import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../../../../../services/api";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import * as Dialog from "@radix-ui/react-dialog";
import SyncLoader from "react-spinners/SyncLoader";
import { format } from "date-fns";
import { useAuth } from "../../../../../../hooks/auth";
import { debounce } from "lodash";
import { useRoutes } from "../../../../../../hooks/routes";

import IHookForm from "../../../../../../shared/dtos/IHookForm";
import IListTemplateApiProps from "./dtos/IListTemplateApiProps";
import IListTemplateDataParams from "./dtos/IListTemplateDataParams";
import IFormSearch from "./dtos/IFormSearch";

import MasterAsideControls from "../../../../components/SidebarMaster/MasterAsideControls";
import HeaderMaster from "../../../../components/SidebarMaster/HeaderMaster";
import MasterContainer from "../../../../components/SidebarMaster/MasterContainer";
import MasterMain from "../../../../components/SidebarMaster/MasterMain";

import DialogModal from "../../../../../../shared/components/DialogModal";
import Button from "../../../../../../shared/components/Button";
import Input from "../../../../../../shared/components/Input";
import IconSvg from "../../../../../../shared/components/IconSvg";
import Pagination from "../../../../../../shared/components/Pagination";
import Tooltip from "../../../../../../shared/components/Tooltip";
import TooltipImage from "../../../../../../shared/components/TooltipImage";

import penIcon from "../../../../../../shared/assets/pen-icon.svg";
import trashIcon from "../../../../../../shared/assets/trash-icon.svg";
import iconSearchWhite from "../../../../../../shared/assets/icon-search-white.svg";

import { Notify } from "../../../../../../shared/utils";

import {
  BoxList,
  ButtonFlexBox,
  ButtonSearch,
  FlexBox,
  HStack,
  Main,
  Form,
  ContainerButtons,
  FlexPagination,
  ContainerLoading,
  Table,
  TableTh,
  TableTr,
  TableTd,
  TableThead,
  TableTbody,
  TableTdText,
  TextNegative,
  TextPositive,
} from "./styles";

const ListTemplate: React.FC = () => {
  const location = useLocation();
  const dataLocation: any = location.state;

  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [dataTemplateList, setDataTemplateList] =
    useState<IListTemplateApiProps>({
      pagination: 0,
      dataArray: [],
    });
  const [listTemplateDataParams, setListTemplateDataParams] =
    useState<IListTemplateDataParams>({
      pagination:
        dataLocation && dataLocation.params != null
          ? dataLocation.params.pagination
          : 0,
    });

  const { data: dataUseAuth } = useAuth();

  const { getUrl } = useRoutes();

  const userPermissionCreate =
    dataUseAuth.admin_user_permissions.create_templates;
  const userPermissionRead = dataUseAuth.admin_user_permissions.read_templates;
  const userPermissionUpdate =
    dataUseAuth.admin_user_permissions.update_templates;
  const userPermissionDelete =
    dataUseAuth.admin_user_permissions.delete_templates;
  const userPermissionClone =
    dataUseAuth.admin_user_permissions.clone_templates;
  const userPermissionCloneToUser =
    dataUseAuth.admin_user_permissions.clone_bits_to_user_template;

  const navigate = useNavigate();

  useEffect(() => {
    getUrl("/templates");
  }, []);

  const handleCreateTemplate = useCallback(() => {
    navigate("/create-template", {
      state: { params: listTemplateDataParams },
    });
  }, [navigate, listTemplateDataParams]);

  const handleCloneTemplate = useCallback(() => {
    navigate("/clone-template", {
      state: { params: listTemplateDataParams },
    });
  }, [navigate, listTemplateDataParams]);

  const handleCloneTemplateToUser = useCallback(() => {
    navigate("/clone-template-to-user", {
      state: { params: listTemplateDataParams },
    });
  }, [navigate, listTemplateDataParams]);

  useEffect(() => {
    (async () => {
      const response = await api.get("/bits-templates", {
        params: listTemplateDataParams,
      });

      setDataTemplateList(response.data);
      setTotalPages(response.data.pagination);
      setLoading(false);
    })();
  }, [reload, listTemplateDataParams]);

  const handleDeleteTemplate = useCallback(
    async (id: string) => {
      await api.delete(`/bits-templates/${id}`);
      setReload(!reload);
    },
    [reload]
  );

  const formSchema = yup.object().shape({
    search: yup.string(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IHookForm>({
    mode: "onChange",
    resolver: yupResolver(formSchema),
  });

  const onSubmit = useCallback(
    debounce((formData: string) => {
      try {
        setLoading(true);

        if (formData === "") {
          setListTemplateDataParams({
            pagination: 0,
          });
        } else {
          setListTemplateDataParams({
            pagination: 0,
            search: formData,
          });
        }
      } catch (err: any) {
        console.log(err?.response?.data);
      }
    }, 400),
    []
  );

  const navigateView = useCallback(
    (item) => {
      navigate(`/view-template/${item.id}`, {
        state: { params: listTemplateDataParams },
      });
    },
    [navigate, listTemplateDataParams]
  );

  const notifyRead = useCallback(() => {
    Notify({ text: "Você não tem permissão de visualizar", type: "error" });
  }, []);

  const handleInputSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchTerm = event.target.value;

      onSubmit(searchTerm);
    },
    [onSubmit]
  );

  return (
    <MasterContainer>
      <MasterAsideControls />
      <HeaderMaster namePage="Lista de templates" />
      <MasterMain>
        <Main>
          <ContainerButtons>
            <Button
              onClick={handleCreateTemplate}
              disabled={!userPermissionCreate}
            >
              Criar template
            </Button>

            <Button
              onClick={handleCloneTemplate}
              disabled={!userPermissionClone}
              style={{ marginLeft: 10 }}
            >
              Clonar template para bits
            </Button>

            <Button
              onClick={handleCloneTemplateToUser}
              disabled={!userPermissionCloneToUser}
              style={{ marginLeft: 10 }}
            >
              Clonar template para o usuário
            </Button>
          </ContainerButtons>

          <HStack>
            <Input
              onChangeInput={handleInputSearchChange}
              label="search"
              register={register}
              required={true}
              placeholder="Pesquisar"
              style={{ width: "100%" }}
            />
          </HStack>

          <BoxList>
            {loading && (
              <ContainerLoading>
                <SyncLoader loading={loading} color="#663399" size={10} />
              </ContainerLoading>
            )}

            {!loading && (
              <Table>
                <TableThead>
                  <TableTr>
                    <TableTh>Título</TableTh>
                    <TableTh>QTD utilizado</TableTh>
                    <TableTh>Está ativo?</TableTh>
                    <TableTh>Está editável?</TableTh>
                    <TableTh>Tipo do documento</TableTh>
                    <TableTh>Data de criação</TableTh>
                    <TableTh>Última modificação</TableTh>
                    <TableTh>Opções</TableTh>
                  </TableTr>
                </TableThead>

                <TableTbody>
                  {dataTemplateList.dataArray.map((item, index) => (
                    <TableTr key={item.id}>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TooltipImage
                          srcImage={item.thumbnail_url}
                          placement="top"
                          widthImage={130}
                          heightImage={184}
                        >
                          <TableTdText>{item.title}</TableTdText>
                        </TooltipImage>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>{item.usage_counter}</TableTdText>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        {item.is_active ? (
                          <TableTdText>
                            <TextPositive>Sim</TextPositive>
                          </TableTdText>
                        ) : (
                          <TableTdText>
                            <TextNegative>Não</TextNegative>
                          </TableTdText>
                        )}
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        {item.is_edit ? (
                          <TableTdText>
                            <TextPositive>Sim</TextPositive>
                          </TableTdText>
                        ) : (
                          <TableTdText>
                            <TextNegative>Não</TextNegative>
                          </TableTdText>
                        )}
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        {item.template_type === "premium" ? (
                          <TableTdText>Premium</TableTdText>
                        ) : (
                          <TableTdText>Básico</TableTdText>
                        )}
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>
                          {format(
                            new Date(item.created_at),
                            "dd/MM/yyyy HH:mm"
                          )}
                        </TableTdText>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>
                          {format(
                            new Date(item.updated_at),
                            "dd/MM/yyyy HH:mm"
                          )}
                        </TableTdText>
                      </TableTd>

                      <TableTd>
                        <FlexBox>
                          <Tooltip title="Editar template" placement="top">
                            <ButtonFlexBox
                              disabled={!userPermissionUpdate}
                              onClick={() => {
                                navigate(`/edit-template/${item.id}`, {
                                  state: { params: listTemplateDataParams },
                                });
                              }}
                            >
                              <IconSvg width="18" height="18" image={penIcon} />
                            </ButtonFlexBox>
                          </Tooltip>

                          <Dialog.Root>
                            <Tooltip title="Deletar template" placement="top">
                              <Dialog.Trigger asChild>
                                <ButtonFlexBox disabled={!userPermissionDelete}>
                                  <IconSvg
                                    width="18"
                                    height="18"
                                    image={trashIcon}
                                  />
                                </ButtonFlexBox>
                              </Dialog.Trigger>
                            </Tooltip>

                            <DialogModal
                              title="Deletar template"
                              subtitle={item.title}
                              type="delete"
                              callback={() => handleDeleteTemplate(item.id)}
                            />
                          </Dialog.Root>
                        </FlexBox>
                      </TableTd>
                    </TableTr>
                  ))}
                </TableTbody>
              </Table>
            )}
          </BoxList>
          <FlexPagination>
            {dataTemplateList.dataArray.length > 0 && (
              <Pagination
                total={totalPages}
                offset={listTemplateDataParams.pagination}
                setDataParams={setListTemplateDataParams}
                dataParams={listTemplateDataParams}
              />
            )}
          </FlexPagination>
        </Main>
      </MasterMain>
    </MasterContainer>
  );
};

export default ListTemplate;
